import React, { useState, useEffect } from 'react';
import styles from './DashboardCards.module.css';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const DashboardCards = ({ clients = [], transactions = [], otherRevenues = [], expenses = [], onExpenseClick }) => {
  const [showSecret, setShowSecret] = useState(false);

  const [selectedMonthYear, setSelectedMonthYear] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    let codeStr = '';
    const handleKeyDown = (e) => {
      codeStr = (codeStr + e.key).slice(-5);
      if (codeStr.toLowerCase() === 'admin') {
        setShowSecret(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const today = new Date();
  const todayDateString = today.toDateString();

  const [yearStr, monthStr] = selectedMonthYear.split('-');
  const selectedYear = parseInt(yearStr, 10);
  const selectedMonth = parseInt(monthStr, 10) - 1;

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentMonthName = monthNames[selectedMonth];

  const totalClients = clients.length;
  
  const onboardToday = clients.filter(c => new Date(c.createdAt || c.startDate).toDateString() === todayDateString).length;
  const totalRemaining = clients.reduce((sum, c) => sum + (parseFloat(c.remainingAmount) || 0), 0);

  let clientRevenueToday = 0;
  let otherRevenueToday = 0;
  let todayRevenue = 0;
  let monthlyRevenue = 0;
  let allTimeRevenue = 0;
  let monthlyExpenses = 0;
  
  expenses.forEach(item => {
     const amt = parseFloat(item.amount) || 0;
     const itemDate = new Date(item.date);
     if (itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === selectedYear) {
       monthlyExpenses += amt;
     }
  });

  // Net Monthly calculation properly deferred after revenue accumulation

  const clientTxMap = {};
  transactions.forEach(tx => {
     if (tx.clientId) clientTxMap[tx.clientId] = true;
  });

  const allIncomes = [...transactions, ...otherRevenues];
  allIncomes.forEach(item => {
     const amt = parseFloat(item.amount) || 0;
     const itemDate = new Date(item.date);
     
     allTimeRevenue += amt;
     
     if (itemDate.getMonth() === selectedMonth && itemDate.getFullYear() === selectedYear) {
       monthlyRevenue += amt;
     }
     
     if (itemDate.toDateString() === todayDateString) {
       todayRevenue += amt;
       if (item.clientId) clientRevenueToday += amt;
       else otherRevenueToday += amt;
     }
  });

  clients.forEach(c => {
     if (!clientTxMap[c.id]) {
       const received = parseFloat(c.receivedAmount || 0);
        if (!isNaN(received) && received > 0) {
         allTimeRevenue += received;
         // Do not add cumulative receivedAmount to daily/monthly revenue 
         // as it skews the charts when lastPaymentDate updates.
       }
     }
  });

  const monthlyNet = monthlyRevenue - monthlyExpenses;

  const cardsData = [
    {
      id: 1,
      title: 'Today (Clients)',
      value: formatCurrency(clientRevenueToday),
      icon: '👥',
      colorClass: styles.blueIcon,
      subtitle: 'users revenue'
    },
    {
      id: 2,
      title: 'Today (Other)',
      value: formatCurrency(otherRevenueToday),
      icon: '🎁',
      colorClass: styles.purpleIcon,
      subtitle: 'other revenue'
    },
    {
      id: 3,
      title: 'Total Today',
      value: formatCurrency(todayRevenue),
      icon: '💵',
      colorClass: styles.greenIcon,
      subtitle: 'all sources'
    },
    {
      id: 4,
      title: 'Monthly Revenue',
      value: formatCurrency(monthlyRevenue),
      icon: '📈',
      colorClass: styles.greenIcon,
      subtitle: currentMonthName,
      isMonthSelector: true
    },
    {
      id: 5,
      title: 'Total Pending',
      value: formatCurrency(totalRemaining),
      icon: '⏳',
      colorClass: styles.yellowIcon,
      subtitle: 'across all clients'
    },
    {
      id: 6,
      title: 'Monthly Expense',
      value: formatCurrency(monthlyExpenses),
      icon: '📉',
      colorClass: styles.redIcon,
      subtitle: currentMonthName,
      onClick: onExpenseClick
    },
    {
      id: 7,
      title: 'Net Monthly Income',
      value: formatCurrency(monthlyNet),
      icon: '🏛️',
      colorClass: monthlyNet >= 0 ? styles.greenIcon : styles.redIcon,
      subtitle: 'revenue - expenses'
    },
    {
      id: 8, 
      title: 'Total Income',
      value: formatCurrency(allTimeRevenue),
      icon: '💰',
      colorClass: styles.purpleIcon || styles.blueIcon,
      subtitle: 'all time revenue'
    }
  ];

  return (
    <div className={styles.cardsGrid}>
      {cardsData.map((card) => (
        <div key={card.id} className={`${styles.card} ${card.onClick ? styles.clickableCard : ''}`} onClick={card.onClick}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitleRow}>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              {card.isMonthSelector && (
                <input 
                  type="month" 
                  value={selectedMonthYear} 
                  onChange={(e) => setSelectedMonthYear(e.target.value)} 
                  className={styles.monthInput}
                />
              )}
            </div>
            <div className={`${styles.cardIcon} ${card.colorClass}`}>
              {card.icon}
            </div>
          </div>
          <p className={styles.cardValue}>{card.value}</p>
          {card.subtitle && (
            <p className={styles.cardSubtitle}>{card.subtitle}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default DashboardCards;
